import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import SignatureAdopt from "../onboarding/SignatureAdopt.jsx";
import { fmtTime, fmtDate, BRAND, GOLD } from "../onboarding/helpers.js";

const STATUS = {
  in_progress: ["In progress", "bg-blue-50 text-blue-700"],
  employee_complete: ["Ready to certify", "bg-amber-50 text-amber-800"],
  certified: ["Certified", "bg-green-50 text-green-700"],
  paper_requested: ["Paper requested", "bg-gray-100 text-gray-700"],
};
export const StatusPill = ({ status }) => {
  const [label, cls] = STATUS[status] || [status, "bg-gray-100 text-gray-700"];
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>{label}</span>;
};

function StartPacket({ employees, onCreated, presetEmployee }) {
  const [form, setForm] = useState({ employee_id: presetEmployee || "", fte: "0", pay_rate: "", start_date: "", reports_to: "Jacob Abeka", location: "Bedford, NH" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const emp = employees.find((e) => e.employee_id === form.employee_id);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await api.onboarding.hr.create({ ...form, hire_date: form.start_date || emp?.employment_date || "", position: emp?.position || "Caregiver" });
      setResult(r);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  if (result) return (
    <div className="bg-white border rounded-xl p-5 space-y-3">
      <h2 className="font-bold text-lg" style={{ color: BRAND }}>Packet started for {emp ? `${emp.first_name} ${emp.last_name}` : "employee"}</h2>
      <p className="text-sm text-gray-700">The new hire signs in to the portal or the JyBek mobile app and opens <b>New Hire Packet</b>. {result.email_sent ? "An email with the link was sent." : "Email is not configured or failed — tell them to sign in directly."}</p>
      {result.portal_account_created && (
        <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">
          A portal login was created. Temporary password: <code className="font-mono font-bold">{result.portal_temp_password}</code>
          <div className="text-xs text-amber-800 mt-1">Give this to the new hire by phone or text, not in the same email. It is shown only once.</div>
        </div>
      )}
      <p className="text-sm text-gray-700">Next: open the packet and <b>countersign the offer letter (Form 02)</b> — the new hire cannot accept it until you sign.</p>
      <button onClick={() => onCreated(result.id)} className="px-4 py-2 rounded-lg text-white font-semibold" style={{ backgroundColor: BRAND }}>Open packet</button>
    </div>
  );

  return (
    <form onSubmit={submit} className="bg-white border rounded-xl p-5 space-y-4">
      <div>
        <h2 className="font-bold text-lg" style={{ color: BRAND }}>Start a New Hire Packet</h2>
        <p className="text-sm text-gray-500">Pick the employee record (add the person under Employees first). The offer details pre-fill Form 02.</p>
      </div>
      <label className="block text-sm">Employee
        <select required value={form.employee_id} onChange={set("employee_id")} className="mt-1 w-full border rounded-lg px-3 py-2 bg-white">
          <option value="">Select…</option>
          {employees.map((e) => <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name} · {e.position || e.staff_category || ""} · {e.employee_id}</option>)}
        </select>
      </label>
      {emp && !(emp.work_email || emp.email || emp.personal_email) && <div className="text-sm text-red-700">This employee has no email on file — add one so they can sign in.</div>}
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-sm">Schedule
          <select value={form.fte} onChange={set("fte")} className="mt-1 w-full border rounded-lg px-3 py-2 bg-white">
            <option value="0">Full-time (40 hrs/week)</option>
            <option value="1">Part-time (under 40 hrs/week)</option>
          </select>
        </label>
        <label className="text-sm">Base pay rate ($/hour)
          <input value={form.pay_rate} onChange={set("pay_rate")} inputMode="decimal" className="mt-1 w-full border rounded-lg px-3 py-2" placeholder="18.00" />
        </label>
        <label className="text-sm">Anticipated start date
          <input type="date" value={form.start_date} onChange={set("start_date")} className="mt-1 w-full border rounded-lg px-3 py-2" />
        </label>
        <label className="text-sm">Reports to
          <input value={form.reports_to} onChange={set("reports_to")} className="mt-1 w-full border rounded-lg px-3 py-2" />
        </label>
        <label className="text-sm sm:col-span-2">Location
          <input value={form.location} onChange={set("location")} className="mt-1 w-full border rounded-lg px-3 py-2" />
        </label>
      </div>
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
      <button disabled={busy || !form.employee_id} className="px-5 py-2.5 rounded-lg text-white font-semibold disabled:opacity-50" style={{ backgroundColor: BRAND }}>{busy ? "Starting…" : "Start packet"}</button>
    </form>
  );
}

export function HRSignaturePage() {
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [me, setMe] = useState(null);
  useEffect(() => { api.onboarding.hr.signer().then(setS); api.me().then(setMe); }, []);
  if (!s || !me) return <div className="text-gray-500">Loading…</div>;
  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <Link to="/admin/onboarding" className="text-sm hover:underline" style={{ color: BRAND }}>← New Hire Packets</Link>
      <div className="bg-white border rounded-2xl p-6 space-y-4">
        <h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>My HR signature</h1>
        {s.adopted && (
          <div className="flex items-center gap-4 border rounded-lg p-3">
            <img src={s.images.sig} alt="signature" className="h-10" /><img src={s.images.init} alt="initials" className="h-8" />
            <div className="text-sm text-gray-600">{s.name} · {s.title}</div>
          </div>
        )}
        <p className="text-sm text-gray-600">{s.adopted ? "Adopting again replaces your signature for future countersignatures; signatures already applied keep the image used at the time." : "Countersignatures on offer letters, training records and certifications use this signature."}</p>
        <SignatureAdopt withTitle defaultName={s.name || me.name} defaultInitials={s.initials} defaultTitle={s.title}
          legal="I agree that this is my electronic signature and initials, used on behalf of Feel At Home Care LLC dba Jybek HomeCare Services when I countersign employee documents."
          submitLabel="Adopt HR signature"
          onAdopt={async (b) => { await api.onboarding.hr.adoptSigner(b); nav("/admin/onboarding"); }} />
      </div>
    </div>
  );
}

export default function AdminOnboarding() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState("");
  const [showStart, setShowStart] = useState(Boolean(params.get("employee")));
  const [q, setQ] = useState("");

  useEffect(() => {
    api.onboarding.hr.list().then(setData).catch((e) => setError(e.message));
    api.employees.list().then(setEmployees).catch(() => {});
  }, []);

  const withoutPacket = useMemo(() => {
    const have = new Set((data?.packets || []).map((p) => p.employee_id));
    return employees.filter((e) => !have.has(e.employee_id) && e.employment_status !== "Terminated");
  }, [data, employees]);

  if (error) return <div className="text-red-600">{error}</div>;
  if (!data) return <div className="text-gray-500">Loading…</div>;
  const rows = data.packets.filter((p) => !q || `${p.name} ${p.employee_id} ${p.position}`.toLowerCase().includes(q.toLowerCase()));
  const counts = data.packets.reduce((m, p) => ({ ...m, [p.status]: (m[p.status] || 0) + 1 }), {});
  const annualDue = data.packets.filter((p) => p.annual_due);

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold tracking-[0.15em]" style={{ color: GOLD }}>WORKFORCE &amp; HR · NH DHHS He-P 822.17</div>
          <h1 className="text-2xl font-bold" style={{ color: BRAND }}>New Hire Packets</h1>
          <p className="text-sm text-gray-500">23-form onboarding packet with electronic initials, signatures and a tamper-evident audit trail.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/onboarding/signature" className="px-3 py-2 rounded-lg border text-sm font-semibold" style={{ borderColor: BRAND, color: BRAND }}>My HR signature</Link>
          <button onClick={() => setShowStart((v) => !v)} className="px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: BRAND }}>{showStart ? "Close" : "+ Start packet"}</button>
        </div>
      </div>

      {!data.signer_adopted && (
        <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">You have not adopted an HR signature yet. <Link to="/admin/onboarding/signature" className="underline font-semibold">Adopt it now</Link> so you can countersign.</div>
      )}
      {!data.audit_chain.ok && (
        <div className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-lg p-3">⚠ The onboarding audit trail failed its integrity check at event #{data.audit_chain.bad_id}. Contact your administrator.</div>
      )}

      {showStart && <StartPacket employees={withoutPacket} presetEmployee={params.get("employee") || ""} onCreated={(id) => nav(`/admin/onboarding/${id}`)} />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["in_progress", "In progress"], ["employee_complete", "Ready to certify"], ["certified", "Certified"], ["annual", "Annual due (30 days)"]].map(([k, label]) => (
          <div key={k} className="bg-white border rounded-xl p-4">
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-2xl font-bold" style={{ color: BRAND }}>{k === "annual" ? annualDue.length : counts[k] || 0}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="p-3 border-b flex items-center justify-between gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or ID" className="border rounded-lg px-3 py-1.5 text-sm w-full sm:w-72" />
          <span className="text-xs text-gray-500 hidden sm:block">Audit trail {data.audit_chain.ok ? "✓ intact" : "✗ check failed"}</span>
        </div>
        {rows.length === 0 ? <div className="p-6 text-sm text-gray-500">No packets yet. Start one for a new hire.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-gray-600 text-left">
                <tr><th className="px-4 py-2">Employee</th><th className="px-4 py-2">Hire date</th><th className="px-4 py-2">Progress</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Updated</th></tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/admin/onboarding/${p.id}`)}>
                    <td className="px-4 py-3"><div className="font-medium" style={{ color: BRAND }}>{p.name}</div><div className="text-xs text-gray-500">{p.position} · {p.employee_id}</div></td>
                    <td className="px-4 py-3">{p.hire_date || "—"}</td>
                    <td className="px-4 py-3 min-w-[140px]">
                      <div className="text-xs text-gray-600 mb-1">{p.done}/{p.total} forms</div>
                      <div className="h-1.5 bg-gray-100 rounded-full"><div className="h-full rounded-full" style={{ width: `${Math.round((p.done / p.total) * 100)}%`, backgroundColor: BRAND }} /></div>
                    </td>
                    <td className="px-4 py-3"><StatusPill status={p.status} />{p.annual_due && <div className="text-[11px] text-amber-700 mt-1">Annual attestation due {fmtDate(p.annual_due + "T12:00:00Z")}</div>}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{p.certified_at ? `Certified ${fmtTime(p.certified_at)}` : p.completed_at ? `Completed ${fmtTime(p.completed_at)}` : `Started ${fmtTime(p.created_at)}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
